import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import _ from 'lodash';
import { Loader, Select, Statistic, Table } from 'semantic-ui-react';
import { Dash } from 'components/Layout';
import RangeSlider from 'components/RangeSlider';
import { ITALIAN_MONTH_NAMES } from 'shared/constants';
import DashboardNetwork from 'shared/services/rpc/dashboard-network';

const FiltersContainer = styled.div`
  background: #efefef;
  border: 1px solid #ccc;
  padding: 11px;
  padding-bottom: 10px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 16px;
  gap: 16px;

  .dropdown-container {
    width: 100%;
    max-width: 450px;

    span {
      font-weight: bolder;
      padding-left: 2px;
    }
  }

  .buttons-collection {
    display: flex;
    width: 150px;
  }
`;

const StatisticsContainer = styled.div`
  background: #f9fafb;
  border: 1px solid #22242626;
  border-bottom: none;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  padding: 11px;
  display: flex;
  justify-content: center;
  margin-top: 16px;
  gap: 16px;
`;

const yearList = _.range(2023, new Date().getFullYear() + 1).map((value) => ({ key: value, text: value, value }));
const monthLabels = ITALIAN_MONTH_NAMES.reduce(
  (carry, item, index) => ({ ...carry, [index + 1]: item.slice(0, 3) }),
  {}
);

export class CollaboratoriInOutTable extends React.Component {
  constructor(props) {
    super(props);
    const today = new Date();

    this.state = {
      inOutData: [],
      inOutTotals: {},
      isLoading: true,
      year: today.getFullYear(),
      startMonth: 1,
      endMonth: today.getMonth() + 1,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.raId !== this.props.raId) {
      this.loadData();
    }
  }

  onMonthPickerChange = (value) =>
    this.setState({ isLoading: true, startMonth: value.min, endMonth: value.max }, () => {
      if (typeof this.pickerChangeTimeout === 'number') {
        clearTimeout(this.pickerChangeTimeout);
      }
      this.pickerChangeTimeout = setTimeout(this.loadData, 500);
    });

  onYearChange = (e, { value }) => this.setState({ year: value }, this.loadData);

  getMonthRange = () => {
    const range = _.range(this.state.startMonth || 0, (this.state.endMonth || 0) + 1);
    const months = range.reduce((carry, month) => [...carry, { month, name: ITALIAN_MONTH_NAMES[month - 1] }], []);
    if (months.length > 1) {
      months.push({ month: 'tot', name: 'Totali' });
    }
    return months;
  };

  // eslint-disable-next-line no-mixed-operators
  getAllColumnsCount = () => this.getMonthRange().length * 2 + 2;

  getTotalByInOutFlag = (flag) => (this.state.isLoading ? <Dash /> : this.state.inOutTotals[flag]);

  pickerChangeTimeout = undefined;

  loadData = () => {
    const { raId } = this.props;
    const { year, startMonth, endMonth } = this.state;

    this.setState({ isLoading: true });

    DashboardNetwork.getDashboardData('collaboratori-in-out-data', { year, startMonth, endMonth, raId })
      .then((res) => this.setState({ inOutData: res.data, inOutTotals: res.totals }))
      .catch(() =>
        this.props.notification().addNotification({
          message: 'Errore nel caricamento dati della dashboard',
          level: 'error',
        })
      )
      .finally(() => this.setState({ isLoading: false }));
  };

  render() {
    const { year, startMonth, endMonth } = this.state;
    const pickerValue = { min: startMonth, max: endMonth };
    const monthRange = this.getMonthRange();
    const monthsOnly = monthRange.filter((m) => m.month !== 'tot');
    const isSingleMonth = monthsOnly.length === 1;

    return (
      <div>
        <FiltersContainer>
          <div className="dropdown-container">
            <span>Anno</span>
            <Select
              fluid
              placeholder="Seleziona un anno"
              options={yearList}
              value={year}
              onChange={this.onYearChange}
            />
          </div>

          <div className="dropdown-container">
            <span>Mesi</span>
            <RangeSlider
              min={1}
              max={12}
              step={1}
              stepLabels={monthLabels}
              color="#2185d0"
              value={JSON.stringify(pickerValue)}
              onChange={this.onMonthPickerChange}
            />
          </div>
        </FiltersContainer>

        <StatisticsContainer>
          <Statistic color="blue" size="small" style={{ margin: '0 1.5em' }}>
            <Statistic.Value>{this.getTotalByInOutFlag('in')}</Statistic.Value>
            <Statistic.Label>Totale ingressi collaboratori</Statistic.Label>
          </Statistic>

          <Statistic color="orange" size="small" style={{ margin: '0 1.5em' }}>
            <Statistic.Value>{this.getTotalByInOutFlag('out')}</Statistic.Value>
            <Statistic.Label>Totale uscite collaboratori</Statistic.Label>
          </Statistic>
        </StatisticsContainer>

        <div style={{ overflowX: 'auto' }}>
          <Table celled structured style={{ borderBottomLeftRadius: '3px', borderBottomRightRadius: '3px' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell rowSpan="2">Responsabile Area</Table.HeaderCell>

                {this.getMonthRange().map(({ month, name }) => (
                  <Table.HeaderCell key={`month${month}`} colSpan={month === 'tot' ? 3 : 2} textAlign="center">
                    {name}
                  </Table.HeaderCell>
                ))}

                {isSingleMonth && (
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Saldo
                  </Table.HeaderCell>
                )}
              </Table.Row>

              <Table.Row>
                {this.getMonthRange().map(({ month }) => [
                  <Table.HeaderCell key={`monthIn${month}`} textAlign="center">
                    Ingressi
                  </Table.HeaderCell>,
                  <Table.HeaderCell key={`monthOut${month}`} textAlign="center">
                    Uscite
                  </Table.HeaderCell>,
                  ...(month === 'tot'
                    ? [
                        <Table.HeaderCell key={`monthSaldo${month}`} textAlign="center">
                          Saldo
                        </Table.HeaderCell>,
                      ]
                    : []),
                ])}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {this.state.isLoading || !this.state.inOutData.length ? (
                <Table.Row>
                  <Table.Cell textAlign="center" colSpan={this.getAllColumnsCount()}>
                    {this.state.isLoading ? <Loader active inline /> : <i>Nessun dato disponibile.</i>}
                  </Table.Cell>
                </Table.Row>
              ) : (
                this.state.inOutData.map(({ months, responsabileArea }) => {
                  const totalIn = _.sumBy(_.values(months), 'in');
                  const totalOut = _.sumBy(_.values(months), 'out');
                  const saldo = totalIn - totalOut;

                  const saldoColor = saldo > 0 ? '#21ba45' : saldo < 0 ? '#db2828' : '#767676';

                  return (
                    <Table.Row key={`raData${responsabileArea}`}>
                      <Table.Cell>{responsabileArea || <i>(altri)</i>}</Table.Cell>

                     {monthsOnly.map(({ month }) => {
                      const counters = months[month] || { in: 0, out: 0 };

                      return [
                        <Table.Cell key={`ra${responsabileArea}-in${month}`} textAlign="center">
                          {counters.in}
                        </Table.Cell>,
                        <Table.Cell key={`ra${responsabileArea}-out${month}`} textAlign="center">
                          {counters.out}
                        </Table.Cell>,
                      ];
                    })}

                      {!isSingleMonth && (
                        <Table.Cell textAlign="center" style={{ color: '#2185d0' }}>
                          <b>{totalIn}</b>
                        </Table.Cell>
                      )}
                      {!isSingleMonth && (
                        <Table.Cell textAlign="center" style={{ color: '#f2711c' }}>
                          <b>{totalOut}</b>
                        </Table.Cell>
                      )}
                      {!isSingleMonth ? (
                        <Table.Cell textAlign="center" style={{ color: saldoColor }}>
                          <b>{saldo > 0 ? `+${saldo}` : saldo}</b>
                        </Table.Cell>
                      ) : (
                        <Table.Cell textAlign="center" style={{ color: saldoColor }}>
                          <b>{saldo > 0 ? `+${saldo}` : saldo}</b>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  );
                })

              )}
            </Table.Body>
          </Table>
        </div>
      </div>
    );
  }
}

CollaboratoriInOutTable.propTypes = {
  raId: PropTypes.string,
  notification: PropTypes.func,
};

export default CollaboratoriInOutTable;



// WEBPACK FOOTER //
// ./app/components/NetworkDashboard/CollaboratoriInOutTable/index.js